import {
  DEFAULT_PUBLIC_PROVIDER_ERROR,
  sanitizePublicProviderText,
} from "@cmmd-forge/shared/publicProviderCopy";

const TRANSPORT_ERROR_PATTERNS = [
  /\bSocketCloseError\b/i,
  /\bSocketOpenError\b/i,
  /\bSocketReadError\b/i,
  /\bRpcClientDefect:\s*Unknown socket error\b/i,
  /Unable to connect to the Forge server WebSocket\./i,
  /\bping timeout\b/i,
] as const;

/** Whether an error message is socket or transport noise, not something a person did. */
export function isTransportConnectionErrorMessage(message: string | null | undefined): boolean {
  if (typeof message !== "string") {
    return false;
  }

  const normalizedMessage = message.trim();
  if (normalizedMessage.length === 0) {
    return false;
  }

  return TRANSPORT_ERROR_PATTERNS.some((pattern) => pattern.test(normalizedMessage));
}

/**
 * What a thread surface may show for an error that arrived on a stream. Transport noise is
 * dropped, because a live stream reconnects by itself and the noise says nothing to a person.
 */
export function sanitizeThreadErrorMessage(message: string | null | undefined): string | null {
  if (isTransportConnectionErrorMessage(message)) return null;
  return sanitizePublicProviderText(message, { fallback: DEFAULT_PUBLIC_PROVIDER_ERROR });
}

const SEND_CONNECTION_LOSS_PATTERNS = [
  /^Transport disposed$/,
  /WebSocket RPC request timed out after \d+ms\./,
] as const;

const SEND_CONNECTION_LOST_MESSAGE =
  "Lost the connection to this Environment. Your message was not sent.";

/**
 * What to tell a person whose send failed. Thread surfaces drop transport noise, and a failed
 * send has no other voice, so a dropped connection is named here in plain words instead.
 */
export function describeSendFailure(error: unknown): string {
  if (!(error instanceof Error)) return "Failed to send message.";
  const lostConnection =
    isTransportConnectionErrorMessage(error.message) ||
    SEND_CONNECTION_LOSS_PATTERNS.some((pattern) => pattern.test(error.message.trim()));
  return lostConnection ? SEND_CONNECTION_LOST_MESSAGE : error.message;
}
